/**
 * Phase 3.27 — Börsenexporte als aufklappbare Akkordeon-Channels.
 * Phase 3.41 — Eigener Händler-Zugang pro Börse hinzugefügt.
 */
import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { CheckRow } from "./_shared";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export function ExchangeExportSection({ currentUser }) {
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const [drafts, setDrafts] = useState({});

    const flip = async (key, next) => {
        setBusy(true);
        try {
            await api.patch("/users/me/preferences", { [key]: next });
            await refresh();
            toast.success("Gespeichert");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const saveCredentials = async (channelKey) => {
        setBusy(true);
        try {
            const payload = {
                [`export_${channelKey}_username`]: drafts[`${channelKey}_username`] ?? currentUser?.[`export_${channelKey}_username`] ?? "",
                [`export_${channelKey}_password`]: drafts[`${channelKey}_password`] ?? currentUser?.[`export_${channelKey}_password`] ?? "",
                [`export_${channelKey}_dealer_id`]: drafts[`${channelKey}_dealer_id`] ?? currentUser?.[`export_${channelKey}_dealer_id`] ?? "",
            };
            await api.patch("/users/me/preferences", payload);
            await refresh();
            toast.success("Zugangsdaten gespeichert");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const channels = [
        { key: "mobile", label: "mobile.de · Zweiradhändler", hint: "Export zu Deutschlands größter Fahrzeugbörse.", color: "#FFCC00" },
        { key: "autoscout", label: "AutoScout24 · Zweiradhändler", hint: "AutoScout24-Zweiradportal.", color: "#FFCC00" },
        { key: "kleinanzeigen", label: "Kleinanzeigen · Zweiradhändler", hint: "Vormals eBay-Kleinanzeigen — Zweirad-Kategorie.", color: "#FFCC00" },
        { key: "ebay_fahrzeugmarkt", label: "eBay Fahrzeugmarkt", hint: "Motorrad- & Roller-Kategorie auf eBay.de (Fahrzeugmarkt).", color: "#FFCC00" },
        { key: "google", label: "Google Fahrzeuganzeigen", hint: "Export zu Google Vehicle Listings / Fahrzeuganzeigen.", color: "#FFCC00" },
    ];

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="exchange-exports-section">
            <h3 className="font-display font-bold text-lg mb-1">Börsenexporte</h3>
            <p className="text-xs text-gray-600 mb-4">Markieren Sie, an welche externen Fahrzeugbörsen Ihre Inserate exportiert werden sollen.</p>
            <div className="mb-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-sm">
                Aktuell nur Vormerkung – die technische Anbindung zu mobile.de, AutoScout24, Kleinanzeigen, eBay Fahrzeugmarkt und Google Fahrzeuganzeigen wird derzeit vorbereitet. Sie werden informiert, sobald der Sync live geht.
            </div>
            <div className="space-y-3" data-testid="exchange-channels-list">
                {channels.map((c) => {
                    const enabled = !!currentUser?.[`export_${c.key}`];
                    const useOwn = !!currentUser?.[`export_${c.key}_use_own`];
                    return (
                        <div key={c.key} className="border border-gray-200 rounded-sm" data-testid={`exchange-channel-${c.key}`}>
                            <CheckRow
                                label={c.label}
                                hint={c.hint}
                                checked={enabled}
                                disabled={busy}
                                onChange={(v) => flip(`export_${c.key}`, v)}
                                testId={`export-${c.key}-checkbox`}
                            />
                            {enabled && (
                                <div className="border-t border-gray-100 px-5 pb-4 pt-3 bg-gray-50/50 space-y-3" data-testid={`exchange-${c.key}-panel`}>
                                    <CheckRow
                                        label={'Über Zugang „Die Zweiradhändler" nutzen'}
                                        hint="Export erfolgt über den gemeinsamen Plattform-Zugang von twowheels.info — keine eigenen Zugangsdaten nötig."
                                        checked={!!currentUser?.[`export_${c.key}_use_zweiradhaendler`]}
                                        disabled={busy}
                                        onChange={(v) => flip(`export_${c.key}_use_zweiradhaendler`, v)}
                                        testId={`export-${c.key}-use-zweiradhaendler-checkbox`}
                                    />
                                    <CheckRow
                                        label="Eigenen Händler-Zugang nutzen"
                                        hint="Wir exportieren mit Ihren persönlichen Zugangsdaten."
                                        checked={useOwn}
                                        disabled={busy}
                                        onChange={(v) => flip(`export_${c.key}_use_own`, v)}
                                        testId={`export-${c.key}-use-own-checkbox`}
                                    />
                                    {useOwn && (
                                        <div data-testid={`export-${c.key}-credentials`}>
                                            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-[11px] p-2.5 rounded-sm mb-3" data-testid={`export-${c.key}-cost-hint`}>
                                                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                                <span>
                                                    <strong>Hinweis:</strong> Hier entstehen Kosten, die Sie mit Ihrem separaten Konto bei {c.label.split(" · ")[0]} bezahlen müssen.
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Händler-ID / Kundennummer</Label>
                                                    <Input
                                                        defaultValue={currentUser?.[`export_${c.key}_dealer_id`] || ""}
                                                        onChange={(e) => setDrafts((p) => ({ ...p, [`${c.key}_dealer_id`]: e.target.value }))}
                                                        placeholder="z. B. 1234567"
                                                        className="rounded-sm mt-1 text-sm font-mono-num"
                                                        data-testid={`export-${c.key}-dealer-id`}
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Benutzername / Login</Label>
                                                    <Input
                                                        defaultValue={currentUser?.[`export_${c.key}_username`] || ""}
                                                        onChange={(e) => setDrafts((p) => ({ ...p, [`${c.key}_username`]: e.target.value }))}
                                                        placeholder="Ihr Login"
                                                        className="rounded-sm mt-1 text-sm font-mono-num"
                                                        data-testid={`export-${c.key}-username`}
                                                    />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Passwort / API-Token</Label>
                                                    <Input
                                                        type="password"
                                                        defaultValue={currentUser?.[`export_${c.key}_password`] || ""}
                                                        onChange={(e) => setDrafts((p) => ({ ...p, [`${c.key}_password`]: e.target.value }))}
                                                        placeholder="••••••••"
                                                        className="rounded-sm mt-1 text-sm font-mono-num"
                                                        data-testid={`export-${c.key}-password`}
                                                    />
                                                </div>
                                                <div className="sm:col-span-2 flex justify-end">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => saveCredentials(c.key)}
                                                        disabled={busy}
                                                        className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm"
                                                        data-testid={`export-${c.key}-save`}
                                                    >
                                                        Zugangsdaten speichern
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!useOwn && (
                                        <div className="text-[10px] text-gray-500">
                                            Sobald die Schnittstelle live geht, exportieren wir Ihre Inserate über unseren gemeinsamen Plattform-Zugang.
                                        </div>
                                    )}
                                    {/* Feb-2026 (User-Wunsch) — Portal-Händlerbewertungen bei eigenen Inseraten anzeigen */}
                                    <div className="border-t border-gray-100 pt-3">
                                        <CheckRow
                                            label="Händlerbewertungen dieses Portals anzeigen"
                                            hint={`Zeigt Ihre Händlerbewertungen von ${c.label.split(" · ")[0]} bei Ihren Fahrzeug-Inseraten an.`}
                                            checked={!!currentUser?.[`export_${c.key}_show_portal_ratings`]}
                                            disabled={busy}
                                            onChange={(v) => flip(`export_${c.key}_show_portal_ratings`, v)}
                                            testId={`export-${c.key}-show-ratings-checkbox`}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
